import { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from 'react'
import type { ProposalSection } from '@/api/proposals/proposals'
import { CanvasEditActions, isCanvasEditShortcutTarget, useCanvasEditShortcuts } from './CanvasEditActions'
import type { KeyboardEvent } from 'react'

const MarkdownEditor = lazy(() =>
  import('@/components/MarkdownEditor').then((module) => ({ default: module.MarkdownEditor }))
)

export type ProposalCanvasBodyEditorProps = {
  section: ProposalSection
  saving: boolean
  error?: string
  onSave: (draftMarkdown: string) => void
  onCancel: () => void
}

export function ProposalCanvasBodyEditor({ section, saving, error, onSave, onCancel }: ProposalCanvasBodyEditorProps) {
  const [draft, setDraft] = useState(section.draftMarkdown)
  const [validationError, setValidationError] = useState<string>()
  const editorRef = useRef<HTMLElement>(null)
  const focusFallbackRef = useRef<HTMLDivElement>(null)
  const errorId = useId()
  const visibleError = validationError ?? error

  const focusCurrentEditor = useCallback(() => {
    const currentEditor = focusFallbackRef.current?.querySelector<HTMLElement>('.ProseMirror') ?? editorRef.current
    currentEditor?.focus()
  }, [])

  const save = useCallback(() => {
    if (draft.trim().length === 0) {
      setValidationError('Section content cannot be empty')
      focusCurrentEditor()
      return
    }
    setValidationError(undefined)
    onSave(draft)
  }, [draft, focusCurrentEditor, onSave])

  const cancel = useCallback(() => {
    setDraft(section.draftMarkdown)
    setValidationError(undefined)
    onCancel()
  }, [onCancel, section.draftMarkdown])

  const onKeyDown = useCanvasEditShortcuts({ disabled: saving, onSave: save, onCancel: cancel })
  const onKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (saving || event.defaultPrevented || event.nativeEvent.isComposing) return
      const isEditorTarget = isCanvasEditShortcutTarget(event.target, focusFallbackRef.current)
      if (event.key === 'Escape' && isEditorTarget) {
        event.preventDefault()
        event.stopPropagation()
        cancel()
        return
      }
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && isEditorTarget) {
        event.preventDefault()
        event.stopPropagation()
        save()
      }
    },
    [cancel, save, saving]
  )
  const onEditorReady = useCallback((editor: HTMLElement) => {
    editorRef.current = editor
    if (editor instanceof HTMLTextAreaElement) editor.dataset['canvasEditShortcuts'] = 'true'
    editor.focus()
  }, [])

  useEffect(() => {
    focusFallbackRef.current?.focus()
  }, [])

  useEffect(() => {
    if (error !== undefined && !saving) focusCurrentEditor()
  }, [error, focusCurrentEditor, saving])

  return (
    // The group owns editor-wide shortcuts while focus remains in the artifact. The content itself
    // stays a native rich textbox or textarea rather than turning the group into an application role.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="group"
      aria-label={`Editing ${section.heading} body`}
      data-canvas-editing="true"
      onKeyDownCapture={onKeyDownCapture}
      onKeyDown={onKeyDown}
    >
      <div
        ref={focusFallbackRef}
        tabIndex={-1}
        data-testid="proposal-body-editor-focus"
        className="rounded-sm focus-within:ring-2 focus-within:ring-ring/50 focus-within:ring-offset-2 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        <Suspense fallback={<p className="text-sm text-muted-foreground">Loading editor</p>}>
          <MarkdownEditor
            key={section.sectionId}
            defaultValue={section.draftMarkdown}
            onChange={(markdown) => {
              setDraft(markdown)
              setValidationError(undefined)
            }}
            readOnly={saving}
            ariaLabel={`Edit ${section.heading} body`}
            {...(visibleError === undefined ? {} : { ariaDescribedBy: errorId })}
            onReady={onEditorReady}
          />
        </Suspense>
      </div>
      <CanvasEditActions
        state={saving ? 'saving' : 'editing'}
        {...(visibleError === undefined ? {} : { error: visibleError, errorId })}
        onSave={save}
        onCancel={cancel}
      />
    </div>
  )
}
